import { Hash, Type } from "lucide-react";
import { useT } from "@/src/i18n";
import { OrphanedRoomKeys } from "@/src/components/OrphanedRoomKeys";
import { useMapStore } from "@/src/store/useMapStore";

const tabClass = "flex h-9 flex-1 items-center justify-center gap-1.5 rounded-lg text-[10px] font-semibold transition";

export function AnnotationDrawer() {
  const kind = useMapStore((state) => state.annotationKind);
  const text = useMapStore((state) => state.annotationText);
  const fontSize = useMapStore((state) => state.annotationFontSize);
  const color = useMapStore((state) => state.annotationColor);
  const nextRoomNumber = useMapStore((state) => state.nextRoomNumber);
  const repeatPlacement = useMapStore((state) => state.repeatPlacement);
  const setKind = useMapStore((state) => state.setAnnotationKind);
  const setText = useMapStore((state) => state.setAnnotationText);
  const setFontSize = useMapStore((state) => state.setAnnotationFontSize);
  const setColor = useMapStore((state) => state.setAnnotationColor);
  const setNextRoomNumber = useMapStore((state) => state.setNextRoomNumber);
  const setRepeatPlacement = useMapStore((state) => state.setRepeatPlacement);
  const t = useT();

  return (
    <aside className="flex max-h-full w-64 flex-col overflow-hidden rounded-2xl border border-white/10 bg-[#1c1b18]/97 shadow-2xl shadow-black/30 backdrop-blur">
      <div className="shrink-0 border-b border-white/8 px-4 py-3">
        <p className="text-xs font-semibold text-stone-100">{t("annotation.title")}</p>
        <p className="mt-1 text-[10px] leading-4 text-stone-500">{t("annotation.subtitle")}</p>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto">
      <div className="p-3">
        <div className="flex gap-1 rounded-xl bg-white/[0.035] p-1">
          <button type="button" onClick={() => setKind("text")} className={`${tabClass} ${kind === "text" ? "bg-[#a8322c] text-white" : "text-stone-400 hover:text-white"}`} aria-pressed={kind === "text"}><Type size={14} /> {t("annotation.text")}</button>
          <button type="button" onClick={() => setKind("room-number")} className={`${tabClass} ${kind === "room-number" ? "bg-[#a8322c] text-white" : "text-stone-400 hover:text-white"}`} aria-pressed={kind === "room-number"}><Hash size={14} /> {t("annotation.roomNumber")}</button>
        </div>

        <label className="mt-3 block text-[9px] font-bold uppercase tracking-[0.14em] text-stone-600">
          {kind === "text" ? t("annotation.labelText") : t("annotation.nextRoomNumber")}
          {kind === "text" ? (
            <input value={text} onChange={(event) => setText(event.target.value)} maxLength={80} autoFocus className="mt-1.5 h-10 w-full rounded-xl border border-white/10 bg-white/[0.04] px-3 text-xs font-medium normal-case tracking-normal text-stone-100 outline-none transition placeholder:text-stone-600 focus:border-[#c3463d]" placeholder={t("annotation.placeholder")} />
          ) : (
            <input type="number" min={1} max={999} value={nextRoomNumber} onChange={(event) => setNextRoomNumber(Number(event.target.value) || 1)} className="mt-1.5 h-10 w-full rounded-xl border border-white/10 bg-white/[0.04] px-3 text-xs font-medium normal-case tracking-normal text-stone-100 outline-none transition focus:border-[#c3463d]" />
          )}
        </label>

        <div className="mt-3 grid grid-cols-[1fr_auto] gap-2">
          <label className="text-[9px] font-bold uppercase tracking-[0.14em] text-stone-600">{t("annotation.textSize")}
            <select value={fontSize} onChange={(event) => setFontSize(Number(event.target.value))} className="mt-1.5 h-9 w-full rounded-lg border border-white/10 bg-[#26231f] px-2 text-[10px] normal-case tracking-normal text-stone-200 outline-none focus:border-[#c3463d]">
              <option value={16}>{t("annotation.sizeSmall")}</option><option value={24}>{t("annotation.sizeMedium")}</option><option value={34}>{t("annotation.sizeLarge")}</option><option value={48}>{t("annotation.sizeTitle")}</option>
            </select>
          </label>
          <label className="text-[9px] font-bold uppercase tracking-[0.14em] text-stone-600">{t("annotation.color")}
            <input type="color" value={color} onChange={(event) => setColor(event.target.value)} className="mt-1.5 block h-9 w-11 cursor-pointer rounded-lg border border-white/10 bg-transparent p-1" aria-label={t("annotation.colorAria")} />
          </label>
        </div>
      </div>
      {/* Inside the one scrolling body, per D-023: a sibling outside it would
          push the drawer off the screen again the moment orphans exist. */}
      <OrphanedRoomKeys />
      </div>
      <label className="shrink-0 flex cursor-pointer items-center justify-between gap-3 border-t border-white/8 px-4 py-3 text-[10px] text-stone-400">
        <span><strong className="block text-stone-200">{t("annotation.keepPlacing")}</strong>{kind === "room-number" ? t("annotation.keepPlacingNumber") : t("annotation.keepPlacingText")}</span>
        <input type="checkbox" checked={repeatPlacement} onChange={(event) => setRepeatPlacement(event.target.checked)} className="h-4 w-4 accent-[#b4362f]" />
      </label>
    </aside>
  );
}
